import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

export default function ChildCarousel({ children: childCards, activeIndex = 0, onIndexChange }) {
  const [currentIndex, setCurrentIndex] = useState(activeIndex);
  const [direction, setDirection] = useState(null); // 'left' or 'right'
  const [isAnimating, setIsAnimating] = useState(false);

  // Swipe gesture state
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const minSwipeDistance = 50;

  const childCount = Array.isArray(childCards) ? childCards.length : 0;

  // Sync with external activeIndex
  useEffect(() => {
    if (activeIndex !== currentIndex) {
      setCurrentIndex(activeIndex);
    }
  }, [activeIndex]);

  const goToIndex = (index, dir) => {
    if (isAnimating) return;
    const newIndex = Math.max(0, Math.min(index, childCount - 1));
    if (newIndex === currentIndex) return;

    setDirection(dir);
    setIsAnimating(true);

    // After animation starts, update the index
    setTimeout(() => {
      setCurrentIndex(newIndex);
      if (onIndexChange) {
        onIndexChange(newIndex);
      }
    }, 50);

    // Reset animation state after transition completes
    setTimeout(() => {
      setIsAnimating(false);
      setDirection(null);
    }, 350);
  };

  const goToPrevious = () => {
    if (currentIndex > 0) {
      goToIndex(currentIndex - 1, 'right');
    }
  };

  const goToNext = () => {
    if (currentIndex < childCount - 1) {
      goToIndex(currentIndex + 1, 'left');
    }
  };

  // Swipe gesture handlers
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      goToNext();
    } else if (isRightSwipe) {
      goToPrevious();
    }
  };

  // If only one child, render without carousel
  if (childCount <= 1) {
    return (
      <div className="w-full">
        {childCards}
      </div>
    );
  }

  return (
    <div className="relative w-full">
      {/* Navigation arrows */}
      {currentIndex > 0 && (
        <button
          onClick={goToPrevious}
          disabled={isAnimating}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-20 p-2.5 rounded-full bg-white shadow-lg hover:bg-slate-50 transition-all hover:scale-110 -ml-4 border border-slate-200 disabled:opacity-50"
          aria-label="Poprzednie dziecko"
        >
          <ChevronLeft className="w-5 h-5 text-slate-600" />
        </button>
      )}
      {currentIndex < childCount - 1 && (
        <button
          onClick={goToNext}
          disabled={isAnimating}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-20 p-2.5 rounded-full bg-white shadow-lg hover:bg-slate-50 transition-all hover:scale-110 -mr-4 border border-slate-200 disabled:opacity-50"
          aria-label="Następne dziecko"
        >
          <ChevronRight className="w-5 h-5 text-slate-600" />
        </button>
      )}

      {/* Carousel container - shows only current card */}
      <div
        className="relative w-full overflow-hidden touch-pan-y"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <div
          className={cn(
            "w-full transition-all duration-300 ease-out",
            isAnimating && direction === 'left' && "animate-slide-out-left",
            isAnimating && direction === 'right' && "animate-slide-out-right",
            !isAnimating && direction === 'left' && "animate-slide-in-left",
            !isAnimating && direction === 'right' && "animate-slide-in-right"
          )}
          style={{
            transform: isAnimating
              ? direction === 'left' ? 'translateX(-100%)' : 'translateX(100%)'
              : 'translateX(0)',
            opacity: isAnimating ? 0 : 1,
          }}
        >
          {Array.isArray(childCards) && childCards[currentIndex]}
        </div>
      </div>

      {/* Dots indicator */}
      {childCount > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          {Array.from({ length: childCount }).map((_, index) => (
            <button
              key={index}
              onClick={() => {
                if (index !== currentIndex) {
                  goToIndex(index, index > currentIndex ? 'left' : 'right');
                }
              }}
              disabled={isAnimating}
              className={cn(
                "h-2 rounded-full transition-all duration-300",
                index === currentIndex
                  ? "bg-indigo-500 w-6"
                  : "bg-slate-300 hover:bg-slate-400 w-2"
              )}
              aria-label={`Dziecko ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
